/**
 * Utilidades generales para la aplicación
 */

import type { Patient, Session } from '../types';

/**
 * Formatea una fecha a formato legible en español
 * @example formatDate(new Date()) => "18 de abril, 2026"
 */
export function formatDate(date: Date, format: 'long' | 'short' | 'time' = 'long'): string {
  const options: Intl.DateTimeFormatOptions =
    format === 'long'
      ? { year: 'numeric', month: 'long', day: 'numeric' }
      : format === 'short'
        ? { year: 'numeric', month: '2-digit', day: '2-digit' }
        : { hour: '2-digit', minute: '2-digit' };

  return new Date(date).toLocaleDateString('es-AR', options);
}

/**
 * Formatea un timestamp ISO a "Hoy · HH:MM" / "Ayer · HH:MM" / "Vie 1 may · HH:MM"
 * según antigüedad. Usado por listados de sesiones (HistorialSesiones del paciente,
 * SesionesKine del kine) que muestran la hora junto a un ancla de día.
 *
 * @example formatFechaRelativa(today.toISOString()) => "Hoy · 14:32"
 * @example formatFechaRelativa(yesterday.toISOString()) => "Ayer · 09:15"
 * @example formatFechaRelativa(otherDay.toISOString()) => "Vie 1 may · 18:42"
 */
export function formatFechaRelativa(iso: string): string {
  const fecha = new Date(iso);
  const hora = fecha.toLocaleTimeString("es-AR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const hoy = new Date();
  const ayer = new Date(hoy);
  ayer.setDate(hoy.getDate() - 1);

  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (sameDay(fecha, hoy)) return `Hoy · ${hora}`;
  if (sameDay(fecha, ayer)) return `Ayer · ${hora}`;

  const fechaAbs = fecha.toLocaleDateString("es-AR", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
  return `${fechaAbs} · ${hora}`;
}

/**
 * Formatea una fecha relativa (hace X días/horas)
 * @example formatRelativeDate(yesterday) => "Hace 1 día"
 */
export function formatRelativeDate(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - new Date(date).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `Hace ${diffMins} minuto${diffMins !== 1 ? 's' : ''}`;
  if (diffHours < 24) return `Hace ${diffHours} hora${diffHours !== 1 ? 's' : ''}`;
  if (diffDays < 7) return `Hace ${diffDays} día${diffDays !== 1 ? 's' : ''}`;

  return formatDate(date, 'short');
}

/**
 * Calcula la adherencia de un paciente basado en sesiones completadas vs programadas
 * @returns Porcentaje de adherencia (0-100)
 */
export function calcAdherence(completedSessions: number, programmedSessions: number): number {
  if (programmedSessions === 0) return 0;
  return Math.round((completedSessions / programmedSessions) * 100);
}

/**
 * Calcula la adherencia de un paciente en base a sus sesiones
 * TODO: Reemplazar con query agregada real a Supabase
 */
export function calcPatientAdherence(patient: Patient, sessions: Session[]): number {
  const patientSessions = sessions.filter((s) => s.patientId === patient.id);
  const completedCount = patientSessions.filter((s) => s.completada).length;
  const totalCount = patientSessions.length;

  return calcAdherence(completedCount, totalCount);
}

/**
 * Obtiene el color del badge de adherencia según el porcentaje
 */
export function getAdherenceColor(adherence: number): 'success' | 'warning' | 'error' {
  if (adherence >= 80) return 'success';
  if (adherence >= 60) return 'warning';
  return 'error';
}

/**
 * Obtiene las iniciales de un nombre completo
 * @example getInitials("Carlos Méndez") => "CM"
 */
export function getInitials(nombre: string, apellido?: string): string {
  if (apellido) {
    return `${nombre.charAt(0)}${apellido.charAt(0)}`.toUpperCase();
  }
  const parts = nombre.split(' ');
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  }
  return nombre.substring(0, 2).toUpperCase();
}

/**
 * Genera un color consistente basado en un string (para avatars)
 */
export function getColorFromString(str: string): string {
  const colors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-orange-500',
    'bg-teal-500',
    'bg-indigo-500',
    'bg-red-500',
  ];

  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
}

/**
 * Formatea duración en segundos a formato legible
 * @example formatDuration(125) => "2:05"
 */
export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Formatea un timestamp ISO a "HH:MM" 24h. Usado por listados agrupados
 * por fecha donde el header del grupo ya identifica el día — el row solo
 * necesita la hora.
 * @example formatHora("2026-05-01T14:32:00Z") => "14:32"
 */
export function formatHora(iso: string): string {
  return new Date(iso).toLocaleTimeString("es-AR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/**
 * Color por rango de calidad (0-100) para display de sesiones — verde ≥80,
 * ámbar 60-79, rojo <60. null → muted. Usado por listados de sesiones
 * (HistorialSesiones, SesionesKine, tab Sesiones de DetallePaciente).
 */
export function qualityColorClass(pct: number | null): string {
  if (pct === null) return "text-muted-foreground";
  if (pct >= 80) return "text-green-600 dark:text-green-400";
  if (pct >= 60) return "text-amber-600 dark:text-amber-400";
  return "text-red-600 dark:text-red-400";
}

/**
 * Valida formato de email
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Trunca texto a una longitud máxima
 * @example truncate("Lorem ipsum dolor sit amet", 15) => "Lorem ipsum..."
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

/**
 * Calcula edad a partir de fecha de nacimiento
 */
export function calculateAge(birthDate: Date): number {
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  return age;
}

/**
 * Combina clases CSS de forma segura (útil para Tailwind)
 */
export function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ');
}
